import React, { useState } from 'react';
import { Box, Typography, Button, InputBase, IconButton, Menu, MenuItem } from '@mui/material';
import GroupIcon from '@mui/icons-material/Group';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import SearchIcon from '@mui/icons-material/Search';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { GT_COLORS } from '../../../../components/groups-teams/shared/constants';
import { deriveAppStatus, APP_STATUS_META } from '../../../../components/lists/users-list/app-status';
import { UserTypes } from '../../../../types/user.types';

const PAGE_SIZE = 5;

const pulseKeyframes = {
    '@keyframes addMemberPulse': {
        '0%': { boxShadow: `0 0 0 0 ${GT_COLORS.primary}88` },
        '70%': { boxShadow: `0 0 0 18px ${GT_COLORS.primary}00` },
        '100%': { boxShadow: `0 0 0 0 ${GT_COLORS.primary}00` },
    },
};

interface TeamMembersCardProps {
    members: UserTypes.Model[];
    onAddMember?: () => void;
    onRemoveMember?: (userId: string) => void;
}

export const TeamMembersCard: React.FC<TeamMembersCardProps> = ({
    members,
    onAddMember,
    onRemoveMember,
}) => {
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; userId: string } | null>(null);

    // Filter first, then paginate
    const filtered = search
        ? members.filter(
              (m) =>
                  `${m.firstName} ${m.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
                  m.phoneNumber?.includes(search) ||
                  m.employeeId?.toLowerCase().includes(search.toLowerCase())
          )
        : members;

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const safePage = Math.min(page, Math.max(totalPages - 1, 0));
    const filteredMembers = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

    // Reset to page 0 when search changes
    const handleSearch = (value: string) => {
        setSearch(value);
        setPage(0);
    };

    const getInitials = (m: UserTypes.Model) =>
        `${(m.firstName || '?')[0]}${(m.lastName || '?')[0]}`.toUpperCase();


    return (
        <Box
            sx={{
                backgroundColor: GT_COLORS.cardBg,
                border: `1px solid ${GT_COLORS.border}`,
                borderRadius: '10px',
                padding: '20px',
                gridColumn: '1 / -1',
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: '14px' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon sx={{ fontSize: '18px', color: GT_COLORS.primary }} />
                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>Team Members</Typography>
                    <Box
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontWeight: 500,
                            backgroundColor: GT_COLORS.badgeGrayBg,
                            color: GT_COLORS.badgeGrayColor,
                        }}
                    >
                        {members.length}
                    </Box>
                </Box>
                {onAddMember && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<PersonAddIcon sx={{ fontSize: '16px' }} />}
                        onClick={onAddMember}
                        sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            borderRadius: '8px',
                            padding: '6px 12px',
                            ...(members.length === 0
                                ? {
                                    ...pulseKeyframes,
                                    backgroundColor: GT_COLORS.primary,
                                    color: '#fff',
                                    border: `1px solid ${GT_COLORS.primary}`,
                                    animation: 'addMemberPulse 2s ease-in-out infinite',
                                    fontWeight: 600,
                                    '&:hover': { backgroundColor: GT_COLORS.primaryHover },
                                }
                                : {
                                    color: GT_COLORS.textMuted,
                                    borderColor: GT_COLORS.borderInput,
                                }
                            ),
                        }}
                    >
                        Add Member
                    </Button>
                )}
            </Box>

            {/* Search */}
            <Box sx={{ position: 'relative', width: 280, mb: '12px' }}>
                <SearchIcon
                    sx={{
                        position: 'absolute',
                        left: '10px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '18px',
                        color: GT_COLORS.textFaint,
                        pointerEvents: 'none',
                    }}
                />
                <InputBase
                    placeholder="Search members..."
                    value={search}
                    onChange={(e) => handleSearch(e.target.value)}
                    sx={{
                        width: '100%',
                        border: `1px solid ${GT_COLORS.borderInput}`,
                        borderRadius: '8px',
                        padding: '8px 12px 8px 36px',
                        fontSize: '14px',
                        '&.Mui-focused': {
                            borderColor: GT_COLORS.primary,
                            boxShadow: `0 0 0 3px rgba(32,111,165,.1)`,
                        },
                    }}
                />
            </Box>

            {/* Table */}
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                    <tr>
                        {['Name', 'Phone', 'Employee ID', 'Mobile App', ''].map((h) => (
                            <th
                                key={h}
                                style={{
                                    textAlign: 'left',
                                    fontSize: '11px',
                                    fontWeight: 600,
                                    color: GT_COLORS.textMuted,
                                    textTransform: 'uppercase',
                                    letterSpacing: '.5px',
                                    padding: '8px 10px',
                                    borderBottom: `1px solid ${GT_COLORS.border}`,
                                    ...(h === '' ? { width: 40 } : {}),
                                }}
                            >
                                {h}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {filteredMembers.map((m, i) => (
                        <tr key={m.id || i}>
                            <td style={{ padding: '8px 10px', borderBottom: `1px solid ${GT_COLORS.borderLight}` }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box
                                        sx={{
                                            width: 28,
                                            height: 28,
                                            borderRadius: '50%',
                                            backgroundColor: GT_COLORS.primary,
                                            color: '#fff',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: '11px',
                                            fontWeight: 600,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {getInitials(m)}
                                    </Box>
                                    <Box>
                                        <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                                            {m.firstName} {m.lastName}
                                        </Typography>
                                        {m.employeeId && (
                                            <Typography sx={{ fontSize: '11px', color: GT_COLORS.textFaint }}>
                                                ID: {m.employeeId}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </td>
                            <td style={{ padding: '8px 10px', borderBottom: `1px solid ${GT_COLORS.borderLight}`, color: GT_COLORS.textMuted }}>
                                {m.phoneNumber || '—'}
                            </td>
                            <td style={{ padding: '8px 10px', borderBottom: `1px solid ${GT_COLORS.borderLight}`, color: GT_COLORS.textMuted }}>
                                {m.employeeId || '—'}
                            </td>
                            <td style={{ padding: '8px 10px', borderBottom: `1px solid ${GT_COLORS.borderLight}` }}>
                                {(() => {
                                    const meta = APP_STATUS_META[deriveAppStatus(m)];
                                    return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <Box
                                                sx={{
                                                    width: 8,
                                                    height: 8,
                                                    borderRadius: '50%',
                                                    backgroundColor: meta.color,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <Typography sx={{ fontSize: '13px' }}>
                                                {meta.label}
                                            </Typography>
                                        </Box>
                                    );
                                })()}
                            </td>
                            {onRemoveMember && (
                                <td style={{ padding: '8px 10px', borderBottom: `1px solid ${GT_COLORS.borderLight}` }}>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => setMenuAnchor({ el: e.currentTarget, userId: m.id! })}
                                    >
                                        <MoreVertIcon sx={{ fontSize: '16px', color: GT_COLORS.textFaint }} />
                                    </IconButton>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        pt: '12px',
                        mt: '4px',
                        borderTop: `1px solid ${GT_COLORS.border}`,
                    }}
                >
                    <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted }}>
                        Showing {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}{search ? ` (filtered from ${members.length})` : ''} members
                    </Typography>
                    <Box sx={{ display: 'flex', gap: '4px' }}>
                        <PgBtn disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>
                            &larr; Prev
                        </PgBtn>
                        {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => (
                            <PgBtn key={i} active={i === safePage} onClick={() => setPage(i)}>
                                {i + 1}
                            </PgBtn>
                        ))}
                        <PgBtn disabled={safePage === totalPages - 1} onClick={() => setPage(safePage + 1)}>
                            Next &rarr;
                        </PgBtn>
                    </Box>
                </Box>
            )}

            {/* Context menu */}
            <Menu
                anchorEl={menuAnchor?.el}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
            >
                <MenuItem
                    onClick={() => {
                        if (menuAnchor) onRemoveMember?.(menuAnchor.userId);
                        setMenuAnchor(null);
                    }}
                    sx={{ fontSize: '14px', color: GT_COLORS.red }}
                >
                    Remove from team
                </MenuItem>
            </Menu>
        </Box>
    );
};

const PgBtn: React.FC<{
    children: React.ReactNode;
    active?: boolean;
    disabled?: boolean;
    onClick?: () => void;
}> = ({ children, active, disabled, onClick }) => (
    <button
        disabled={disabled}
        onClick={onClick}
        style={{
            padding: '6px 12px',
            border: `1px solid ${active ? GT_COLORS.primary : GT_COLORS.borderInput}`,
            borderRadius: '6px',
            fontSize: '13px',
            fontFamily: 'inherit',
            backgroundColor: active ? GT_COLORS.primary : GT_COLORS.cardBg,
            color: active ? '#fff' : GT_COLORS.textSecondary,
            cursor: disabled ? 'default' : 'pointer',
            opacity: disabled ? 0.4 : 1,
        }}
    >
        {children}
    </button>
);
